/**
 * Clãs (PLANO-DIFERENCIAIS 5.2 / PLANO-ECONOMIA-MONETIZACAO 5.2): sink social + competição,
 * espelhando o PokeIdle. Núcleo puro — sem DOM, sem I/O, sem chamadas de rede (o backend
 * real fica em RPCs/migrations à parte).
 */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const CLAN_JOIN_LEVEL = 80;
/** Trocar de clã (depois do 1º, que é grátis) custa Emerald — sink premium leve. */
export const CLAN_CHANGE_COST_EMERALD = 40;

/** Ranks 2-5 avançam por missão OU pulando com IdleCoin (1,5M/3M/4,5M/6M = 15M total). */
export const CLAN_RANKS = Object.freeze([
  Object.freeze({ rank: 1, name: 'Recruta', skipCost: 0 }),
  Object.freeze({ rank: 2, name: 'Membro', skipCost: 1500000 }),
  Object.freeze({ rank: 3, name: 'Veterano', skipCost: 3000000 }),
  Object.freeze({ rank: 4, name: 'Elite', skipCost: 4500000 }),
  Object.freeze({ rank: 5, name: 'Lenda', skipCost: 6000000 }),
]);

export const MAX_CLAN_RANK = CLAN_RANKS.length;
export const CLAN_ELEMENT_BONUS_PER_RANK = 0.06;
export const CLAN_ELEMENT_BONUS_CAP = 0.30;

function coinsOf(state) {
  if (state?.stats && typeof state.stats.coins === 'number') return state.stats.coins;
  return Number(state?.coins) || 0;
}

function setCoins(state, value) {
  if (state?.stats && typeof state.stats === 'object') state.stats.coins = value;
  else state.coins = value;
}

export function normalizeClan(state) {
  if (!state || typeof state !== 'object') return state;
  if (state.clan && typeof state.clan === 'object') {
    state.clan.rank = clamp(Math.floor(Number(state.clan.rank) || 1), 1, MAX_CLAN_RANK);
  }
  return state;
}

export function canJoinClan(state, trainerLevel) {
  return !state?.clan && (Number(trainerLevel) || 0) >= CLAN_JOIN_LEVEL;
}

/** Primeira entrada é grátis; trocar de clã já filiado custa Emerald. */
export function joinClan(state, clanId, trainerLevel) {
  if (!state || typeof state !== 'object' || !clanId) return { ok: false, error: 'invalid_clan' };
  const isSwitch = !!state.clan;
  if (!isSwitch && (Number(trainerLevel) || 0) < CLAN_JOIN_LEVEL) {
    return { ok: false, error: 'trainer_level_too_low' };
  }
  if (isSwitch) {
    const emerald = Math.max(0, Math.floor(Number(state.emerald) || 0));
    if (emerald < CLAN_CHANGE_COST_EMERALD) return { ok: false, error: 'insufficient_emerald' };
    state.emerald = emerald - CLAN_CHANGE_COST_EMERALD;
  }
  state.clan = { id: clanId, rank: 1, element: null, joinedAt: Date.now() };
  return { ok: true, cost: isSwitch ? CLAN_CHANGE_COST_EMERALD : 0 };
}

/** Pula direto para `targetRank`, pagando a soma dos degraus intermediários em IdleCoin. */
export function skipClanRank(state, targetRank) {
  if (!state?.clan) return { ok: false, error: 'no_clan' };
  const target = clamp(Math.floor(Number(targetRank) || 0), 1, MAX_CLAN_RANK);
  const currentRank = clamp(Math.floor(Number(state.clan.rank) || 1), 1, MAX_CLAN_RANK);
  if (target <= currentRank) return { ok: false, error: 'invalid_target' };
  const cost = CLAN_RANKS.filter((row) => row.rank > currentRank && row.rank <= target)
    .reduce((sum, row) => sum + row.skipCost, 0);
  const balance = coinsOf(state);
  if (balance < cost) return { ok: false, error: 'insufficient_funds', cost };
  setCoins(state, balance - cost);
  state.clan.rank = target;
  return { ok: true, cost, rank: target };
}

/** Bônus de combate do clã pelo elemento escolhido: +6%/rank, teto +30% (rank 5). */
export function clanCombatBonus(state) {
  if (!state?.clan) return 0;
  const rank = clamp(Math.floor(Number(state.clan.rank) || 0), 0, MAX_CLAN_RANK);
  return Math.min(CLAN_ELEMENT_BONUS_CAP, rank * CLAN_ELEMENT_BONUS_PER_RANK);
}

/** Bônus efetivo só se o tipo do golpe/espécie bater com o elemento do clã. */
export function clanElementBonus(state, type) {
  if (!state?.clan?.element || state.clan.element !== type) return 0;
  return clanCombatBonus(state);
}
